import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { notifyProductsChanged } from '../utils/productCacheBus';
import { useMsal } from '@azure/msal-react';

const IngredientsContext = createContext();

export function IngredientsProvider({ children }) {
  const { apiCall } = useApi();
  const { accounts } = useMsal();
  const [categories, setCategories] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [blendedProducts, setBlendedProducts] = useState([]);
  const [pendingIngredientChanges, setPendingIngredientChanges] = useState({});
  const [pendingBlendedProductChanges, setPendingBlendedProductChanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Mirrors ProductsContext.pricingDate. When set, loads ingredients/blended
  // products with ?at=<date> so add-on pricing reflects future schedules.
  const [pricingDate, setPricingDate] = useState(null);

  const isAuthenticated = accounts && accounts.length > 0;
  const atSuffix = pricingDate ? `&at=${encodeURIComponent(pricingDate)}` : '';

  // --- Load functions ---

  const loadCategories = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await apiCall('ingredient-categories-list');
      setCategories(result.categories || []);
    } catch (err) {
      console.error('Failed to load ingredient categories:', err);
      setError(err.message);
    }
  }, [apiCall, isAuthenticated]);

  const loadIngredients = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await apiCall(`ingredients-list?includeInactive=true${atSuffix}`);
      setIngredients(result.ingredients || []);
    } catch (err) {
      console.error('Failed to load ingredients:', err);
      setError(err.message);
    }
  }, [apiCall, isAuthenticated, atSuffix]);

  const loadBlendedProducts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await apiCall(`blended-products-list?includeInactive=true${atSuffix}`);
      setBlendedProducts(result.products || []);
    } catch (err) {
      console.error('Failed to load blended products:', err);
      setError(err.message);
    }
  }, [apiCall, isAuthenticated, atSuffix]);

  const loadPendingIngredientChanges = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await apiCall('ingredients-schedule-list?pending=true');
      const byId = {};
      for (const row of result.schedule || []) {
        // Keep only the earliest pending change per ingredient
        if (!byId[row.ingredient_id] || row.effective_date < byId[row.ingredient_id].effective_date) {
          byId[row.ingredient_id] = row;
        }
      }
      setPendingIngredientChanges(byId);
    } catch (err) {
      console.error('Failed to load pending ingredient price changes:', err);
    }
  }, [apiCall, isAuthenticated]);

  const loadPendingBlendedProductChanges = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const result = await apiCall('blended-products-schedule-list?pending=true');
      const byId = {};
      for (const row of result.schedule || []) {
        if (!byId[row.blended_product_id] || row.effective_date < byId[row.blended_product_id].effective_date) {
          byId[row.blended_product_id] = row;
        }
      }
      setPendingBlendedProductChanges(byId);
    } catch (err) {
      console.error('Failed to load pending blended product price changes:', err);
    }
  }, [apiCall, isAuthenticated]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadCategories(),
        loadIngredients(),
        loadBlendedProducts(),
        loadPendingIngredientChanges(),
        loadPendingBlendedProductChanges(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [loadCategories, loadIngredients, loadBlendedProducts, loadPendingIngredientChanges, loadPendingBlendedProductChanges]);

  // Refetch ingredients + blended products whenever the pricingDate changes,
  // but only if they're already loaded — we don't want to trigger initial
  // loads here.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (ingredients.length > 0) loadIngredients();
    if (blendedProducts.length > 0) loadBlendedProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingDate]);

  // --- Ingredient CRUD ---

  const createIngredient = useCallback(async (data) => {
    const result = await apiCall('ingredients-create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await loadIngredients();
    notifyProductsChanged();
    return result.ingredient;
  }, [apiCall, loadIngredients]);

  const updateIngredient = useCallback(async (id, data) => {
    const result = await apiCall(`ingredients-update?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await loadIngredients();
    notifyProductsChanged();
    return result.ingredient;
  }, [apiCall, loadIngredients]);

  const deleteIngredient = useCallback(async (id) => {
    const result = await apiCall(`ingredients-delete?id=${id}`, {
      method: 'DELETE',
    });
    await loadIngredients();
    notifyProductsChanged();
    return result;
  }, [apiCall, loadIngredients]);

  // --- Blended Product CRUD ---

  const createBlendedProduct = useCallback(async (data) => {
    const result = await apiCall('blended-products-create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await loadBlendedProducts();
    notifyProductsChanged();
    return result.product;
  }, [apiCall, loadBlendedProducts]);

  const updateBlendedProduct = useCallback(async (id, data) => {
    const result = await apiCall(`blended-products-update?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await loadBlendedProducts();
    notifyProductsChanged();
    return result.product;
  }, [apiCall, loadBlendedProducts]);

  const deleteBlendedProduct = useCallback(async (id) => {
    const result = await apiCall(`blended-products-delete?id=${id}`, {
      method: 'DELETE',
    });
    await loadBlendedProducts();
    notifyProductsChanged();
    return result;
  }, [apiCall, loadBlendedProducts]);

  // --- Price scheduling ---

  const scheduleIngredientPriceChange = useCallback(async ({ ingredient_id, cost_per_gallon, shipping_cost_per_gallon, gross_margin_rate, effective_date }) => {
    const result = await apiCall('ingredients-schedule-create', {
      method: 'POST',
      body: JSON.stringify({ ingredient_id, cost_per_gallon, shipping_cost_per_gallon, gross_margin_rate, effective_date }),
    });
    await Promise.all([loadIngredients(), loadPendingIngredientChanges()]);
    notifyProductsChanged();
    return result;
  }, [apiCall, loadIngredients, loadPendingIngredientChanges]);

  const cancelPendingIngredientChange = useCallback(async (scheduleId) => {
    const result = await apiCall(`ingredients-schedule-delete?id=${scheduleId}`, {
      method: 'DELETE',
    });
    await loadPendingIngredientChanges();
    return result;
  }, [apiCall, loadPendingIngredientChanges]);

  const scheduleBlendedProductPriceChange = useCallback(async ({ blended_product_id, ibc_cost_per_gallon, base_cost, blending_cost_per_gallon, target_margin, effective_date }) => {
    const result = await apiCall('blended-products-schedule-create', {
      method: 'POST',
      body: JSON.stringify({ blended_product_id, ibc_cost_per_gallon, base_cost, blending_cost_per_gallon, target_margin, effective_date }),
    });
    await Promise.all([loadBlendedProducts(), loadPendingBlendedProductChanges()]);
    notifyProductsChanged();
    return result;
  }, [apiCall, loadBlendedProducts, loadPendingBlendedProductChanges]);

  const cancelPendingBlendedProductChange = useCallback(async (scheduleId) => {
    const result = await apiCall(`blended-products-schedule-delete?id=${scheduleId}`, {
      method: 'DELETE',
    });
    await loadPendingBlendedProductChanges();
    return result;
  }, [apiCall, loadPendingBlendedProductChanges]);

  // --- Derived data ---

  const addIns = useMemo(() =>
    ingredients.filter(i => i.ingredient_categories?.code === 'add_in'),
    [ingredients]
  );

  const categoriesLoaded = categories.length > 0;

  const surfactants = useMemo(() =>
    ingredients.filter(i => i.ingredient_categories?.code === 'surfactant'),
    [ingredients]
  );

  const nanoparticles = useMemo(() =>
    ingredients.filter(i => i.ingredient_categories?.code === 'nanoparticle'),
    [ingredients]
  );

  const getCategoryByCode = useCallback((code) =>
    categories.find(c => c.code === code) || null,
    [categories]
  );

  // Returns the markup rate (decimal) for a category code, or null if not loaded yet.
  // This drives the two-component pricing formula:
  //   selling_price = cost / (1 - getCategoryMarginRate(code))
  const getCategoryMarginRate = useCallback((code) => {
    const cat = categories.find(c => c.code === code);
    if (!cat || cat.default_margin === null || cat.default_margin === undefined) return null;
    return Number(cat.default_margin);
  }, [categories]);

  const updateCategory = useCallback(async (id, data) => {
    const result = await apiCall(`ingredient-categories-update?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await loadCategories();
    return result.category;
  }, [apiCall, loadCategories]);

  const value = useMemo(() => ({
    // Data
    categories,
    ingredients,
    blendedProducts,
    addIns,
    surfactants,
    nanoparticles,
    pendingIngredientChanges,
    pendingBlendedProductChanges,
    categoriesLoaded,
    // State
    loading,
    error,
    setError,
    // Load
    loadAll,
    loadCategories,
    loadIngredients,
    loadBlendedProducts,
    loadPendingIngredientChanges,
    loadPendingBlendedProductChanges,
    // Ingredient CRUD
    createIngredient,
    updateIngredient,
    deleteIngredient,
    // Blended Product CRUD
    createBlendedProduct,
    updateBlendedProduct,
    deleteBlendedProduct,
    // Category management
    updateCategory,
    // Price scheduling
    scheduleIngredientPriceChange,
    cancelPendingIngredientChange,
    scheduleBlendedProductPriceChange,
    cancelPendingBlendedProductChange,
    // Helpers
    getCategoryByCode,
    getCategoryMarginRate,
    // Date-aware pricing (mirrors ProductsContext.pricingDate)
    pricingDate,
    setPricingDate,
  }), [
    categories,
    ingredients,
    blendedProducts,
    addIns,
    surfactants,
    nanoparticles,
    pendingIngredientChanges,
    pendingBlendedProductChanges,
    categoriesLoaded,
    loading,
    error,
    setError,
    loadAll,
    loadCategories,
    loadIngredients,
    loadBlendedProducts,
    loadPendingIngredientChanges,
    loadPendingBlendedProductChanges,
    createIngredient,
    updateIngredient,
    deleteIngredient,
    createBlendedProduct,
    updateBlendedProduct,
    deleteBlendedProduct,
    updateCategory,
    scheduleIngredientPriceChange,
    cancelPendingIngredientChange,
    scheduleBlendedProductPriceChange,
    cancelPendingBlendedProductChange,
    getCategoryByCode,
    getCategoryMarginRate,
    pricingDate,
    setPricingDate,
  ]);

  return (
    <IngredientsContext.Provider value={value}>
      {children}
    </IngredientsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useIngredientsContext() {
  const context = useContext(IngredientsContext);
  if (!context) {
    throw new Error('useIngredientsContext must be used within an IngredientsProvider');
  }
  return context;
}
